import {
  doc,
  collection,
  getDocFromServer,
  getDocsFromServer,
  query,
  where,
  updateDoc
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/firebase'
import { buildEvaluationRecord } from '@/utils/studentEvaluation.js'
import {
  DISTRICT_SMOKE_PROFILES,
  createFixtureSession,
  fixtureSessionBlockReason,
  withFixtureSession
} from '../fixtureSession.js'
import { DISTRICT_FIXTURE_KINDS, runDistrictAccessChecks } from '../districtAccessWorkflow.js'

export default {
  id: 'districtAccess',
  label: 'District roles and cross-school caseloads',
  description:
    'Five district profiles, real allSchools claims, two disposable campuses: assigned/support/unassigned and Initial students, edits, lists and settings.',
  destructive: true,
  oncePerSweep: true,
  async run(ctx, step) {
    const reason = fixtureSessionBlockReason(ctx)
    if (reason) {
      step.block('district fixtures', reason)
      return
    }
    const io = createFixtureSession(ctx)
    const fixtures = []
    const uid = ctx.user.uid
    const other = `smoke-other-${doc(collection(db, 'students')).id}`
    await withFixtureSession(ctx, step, io, async () => {
      await step(
        'prepare owned, support-only, unrelated and Initial students at both campuses',
        async () => {
          for (const campus of [0, 1])
            for (const kind of DISTRICT_FIXTURE_KINDS) {
              const owned = ['owned', 'initialOwned', 'initialEvalOwned'].includes(kind)
              const evalOwned = kind === 'initialEvalOwned'
              const initial = kind.startsWith('initial')
              const ref = await io.student(campus, {
                app: {
                  studentData: {
                    firstName: 'SMOKE',
                    lastName: kind,
                    plan: kind === 'support504' ? '504' : 'IEP',
                    planStatus: initial ? 'initial' : '',
                    caseManagerId: evalOwned ? '' : owned ? uid : other,
                    districtSupportIds: kind === 'support504' ? [uid] : [],
                    grade: '9'
                  },
                  schedule: { periods: {} },
                  accommodations: { instruction: 'Smoke baseline' }
                }
              })
              fixtures.push({ ref, kind, campus })
              if (evalOwned)
                await io.evaluation(
                  buildEvaluationRecord({
                    studentId: ref.id,
                    schoolId: io.schools[campus].id,
                    evalType: 'IEP',
                    caseManagerId: uid,
                    createdBy: uid,
                    dates: { referral: new Date().toISOString().slice(0, 10) }
                  })
                )
              await httpsCallable(functions, 'refreshStudentAccess')({ studentId: ref.id })
              const data = (await getDocFromServer(ref)).data()
              if (
                data?.app?.staffAccess !== !initial ||
                (data.app.staffIds || []).includes(uid) !== (owned || kind === 'support504')
              )
                throw new Error('Access worker did not produce the expected fixture assignments')
            }
          return 'Twelve synthetic students, including evaluation owners without a plan case manager; no real student assignments changed'
        }
      )
      await runDistrictAccessChecks(
        DISTRICT_SMOKE_PROFILES,
        fixtures,
        {
          as: (profile) => io.as(profile),
          async read(fixture) {
            if (!(await getDocFromServer(fixture.ref)).exists())
              throw new Error('Fixture disappeared')
          },
          edit: (fixture) =>
            updateDoc(fixture.ref, {
              'app.accommodations.instruction': `Smoke edit ${Date.now()}`
            }),
          settings: (profile) => updateDoc(io.schools[0], { lastSmokeProfile: profile.role }),
          async list(profile) {
            const base = [
              where(
                'schoolId',
                'in',
                io.schools.map((s) => s.id)
              )
            ]
            const filters = ['district_admin', 'district_coordinator'].includes(profile.role)
              ? [[]]
              : [
                  [
                    where('app.staffAccess', '==', true),
                    ...(profile.role === 'case_manager_full'
                      ? []
                      : [where('app.staffIds', 'array-contains', uid)])
                  ],
                  [where('app.studentData.caseManagerId', '==', uid)],
                  [where('app.referralOwnerIds', 'array-contains', uid)]
                ]
            const ids = new Set()
            for (const filter of filters) {
              const snap = await getDocsFromServer(
                query(collection(db, 'students'), ...base, ...filter)
              )
              snap.docs.forEach((d) => ids.add(d.id))
            }
            return [...ids]
          }
        },
        step
      )
    })
  }
}
